// Welcome Day (viewer) — a bright, mobile-first orientation app.
//  · Satellite map fills the screen; each event is a unique pin.
//  · A "Next up" island floats at the top: name, time, location, distance, and a
//    dropdown chevron that reveals the rest of the itinerary.
//  · Tapping a pin (or an itinerary row) slides up a detail sheet.
//  · A backpack button opens the scattered-objects resources (WelcomeBackpack).
// Test environment meant to be lifted into UMaps Web later.
import { useEffect, useLayoutEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import { loadWelcomeDay, sortedEvents, prettyTime, timeToMinutes } from './welcomeDay.js';
import { loadPlaces } from './places.js';
import { picUrl } from './placeImages.js';
import { orientationOptions } from './gmap.js';
import { loadAllMapFiles } from '../pathfinder/lib/dataSource.js';
import { buildMapData } from '../pathfinder/lib/graph.js';
import { nearestNode, pointInPolygon } from '../pathfinder/lib/geo.js';
import { findRoute } from '../pathfinder/lib/route.js';
import WelcomeBackpack from './WelcomeBackpack.jsx';
import umLogo from './um-logo.png';
import './welcomeday.css';

const CENTER = { lat: 49.80939016935401, lng: -97.1343494222327 };
const MOVE_THRESHOLD_M = 8;      // GPS must move this far to count as a "significant change"
const REDRAW_DEVIATION_M = 22;   // walk this far OFF the drawn route ⇒ re-solve the path
const GPS_WEAK_M = 30;           // accuracy worse than this (or no fix) ⇒ show the "inaccurate" notice
const DEMO_POS = { lat: 49.8875, lng: -97.1490 }; // fallback "you are here" when no geolocation (~1.5 km off campus)

// Onboarding pointers shown once each (welcome graphic on first launch, tap-a-
// direction hint the first time the directions box is opened). Stored by id.
const HINTS_KEY = 'umaps-wd-hints';
const loadHints = () => { try { return JSON.parse(localStorage.getItem(HINTS_KEY)) || {}; } catch { return {}; } };
const markHint = (id) => { try { const h = loadHints(); h[id] = true; localStorage.setItem(HINTS_KEY, JSON.stringify(h)); } catch { /* ignore */ } };
// Tunnels (floor -1) are treated as Level 1 so tunnel pathways show clearly on
// that level; everything else keeps its own floor (0 = outside).
const dispFloor = f => (f === -1 ? 1 : (f ?? 0));
// Route/level colour by floor level: L1 blue, L2 green, L3 red, L4 yellow (cycles).
const FLOOR_COLORS = ['#0060a8', '#2e9e5b', '#e53935', '#fcb40c'];
const floorColor = (f) => FLOOR_COLORS[(Math.max(1, dispFloor(f)) - 1) % 4];

// Structure polygon colours — matched exactly to the pathfinder (useRouteLayer).
const POLY_COLORS = {
  outline:  { fill: '#ffffff', fillOp: 1,    stroke: '#aaaaaa', strokeW: 0,   z: 1 },
  room:     { fill: '#FCF8EF', fillOp: 0.85, stroke: '#dbdbdb', strokeW: 1.5, z: 2 },
  stair:    { fill: '#c4956a', fillOp: 0.9,  stroke: '#a0724a', strokeW: 1.5, z: 3 },
  elevator: { fill: '#b0bec5', fillOp: 0.9,  stroke: '#78909c', strokeW: 1.5, z: 3 },
  blocked:  { fill: '#000000', fillOp: 0.9,  stroke: '#000000', strokeW: 1.5, z: 2 },
  tunnel:   { fill: '#ffffff', fillOp: 1,    stroke: '#aaaaaa', strokeW: 1,   z: 2 }, // same scheme as building outlines
};

// Custom stair / elevator glyphs (no emojis): rounded square badge + white glyph.
const STAIR_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><rect x="0.5" y="0.5" rx="6" ry="6" width="23" height="23" fill="#5d4037" stroke="#fff" stroke-width="1.5" opacity="0.92"/><path d="M5 18 h4 v-4 h4 v-4 h4 v-4 h2" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const ELEV_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><rect x="0.5" y="0.5" rx="6" ry="6" width="23" height="23" fill="#37474f" stroke="#fff" stroke-width="1.5" opacity="0.92"/><polygon points="8.5,10 12,5.5 15.5,10" fill="#fff"/><polygon points="8.5,14 15.5,14 12,18.5" fill="#fff"/></svg>`;
const shaftSvg = (kind) => (kind === 'elevator' ? ELEV_SVG : STAIR_SVG);
const svgUri = (svg) => 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
// Zoom-responsive size for shaft markers/buttons: a small constant at or below
// zoom 19 (so they aren't oversized on the campus overview), growing past it.
const SHAFT_BASE = 0.72;
const zoomScale = (z) => (z == null || z <= 19 ? SHAFT_BASE : Math.min(SHAFT_BASE + (z - 19) * 0.35, 2));
// Stair/elevator icons + switchers only show once zoomed in enough to see ~2
// buildings; below this (campus overview) only the event stars remain.
const SHAFT_ICON_ZOOM = 18;

// Tappable stair/elevator switcher anchored at a shaft node (google.maps
// OverlayView so it pans/zooms with the map). Tapping switches floors in place.
function createShaftOverlay(map, position, { kind, label, back, onTap, color }) {
  const gmaps = window.google.maps;
  const latLng = new gmaps.LatLng(position.lat, position.lng);
  class Shaft extends gmaps.OverlayView {
    onAdd() {
      const d = document.createElement('div');
      d.className = `wd-shaft ${back ? 'back' : ''} ${kind}`;
      if (color) { d.style.borderColor = color; d.style.boxShadow = `0 8px 22px rgba(20,30,50,0.28), inset 0 0 0 1.5px ${color}`; }
      d.innerHTML = `<span class="wd-shaft-ic">${shaftSvg(kind)}</span><span class="wd-shaft-lbl">${String(label).replace(/[&<>]/g, '')}</span>`;
      d.addEventListener('click', (e) => { e.stopPropagation(); onTap?.(); });
      ['mousedown', 'touchstart', 'pointerdown'].forEach(t => d.addEventListener(t, e => e.stopPropagation()));
      this.div = d;
      this.getPanes().overlayMouseTarget.appendChild(d);
    }
    draw() { const pt = this.getProjection()?.fromLatLngToDivPixel(latLng); if (pt && this.div) { this.div.style.left = `${pt.x}px`; this.div.style.top = `${pt.y}px`; } }
    onRemove() { this.div?.remove(); this.div = null; }
  }
  const o = new Shaft(); o.setMap(map); return { remove: () => o.setMap(null) };
}

// Five-point star path (100×100 viewBox, tip up) for the event pins.
const STAR_PATH = 'M50,7 L61.17,36.63 L92.8,38.1 L68.07,57.87 L76.45,88.4 L50,71 L23.55,88.4 L31.93,57.87 L7.2,38.1 L38.83,36.63 Z';
// A pulsating star pin anchored on the map (OverlayView so it tracks pan/zoom).
// `number` shows inside the star; state (focus/next) drives colour + size + z.
function createStarOverlay(map, position, { number, color, big, z, delay, onClick, levelChip }) {
  const gmaps = window.google.maps;
  const latLng = new gmaps.LatLng(position.lat, position.lng);
  class Star extends gmaps.OverlayView {
    onAdd() {
      const d = document.createElement('div');
      d.className = `wd-star ${big ? 'big' : ''}`;
      d.style.setProperty('--wd-star-col', color);
      d.style.zIndex = String(z);
      d.style.animationDelay = `${delay}s`;
      d.innerHTML =
        `<span class="wd-star-glow"></span>` +
        `<svg class="wd-star-svg" viewBox="0 0 100 100" aria-hidden="true"><path d="${STAR_PATH}"/></svg>` +
        `<span class="wd-star-num">${String(number)}</span>` +
        (levelChip ? `<span class="wd-star-lvl">${String(levelChip).replace(/[&<>]/g, '')}</span>` : '');
      d.addEventListener('click', (e) => { e.stopPropagation(); onClick?.(); });
      ['mousedown', 'touchstart', 'pointerdown'].forEach(t => d.addEventListener(t, e => e.stopPropagation()));
      this.div = d;
      this.getPanes().overlayMouseTarget.appendChild(d);
    }
    draw() { const pt = this.getProjection()?.fromLatLngToDivPixel(latLng); if (pt && this.div) { this.div.style.left = `${pt.x}px`; this.div.style.top = `${pt.y}px`; } }
    onRemove() { this.div?.remove(); this.div = null; }
  }
  const o = new Star(); o.setMap(map); return { remove: () => o.setMap(null) };
}

// Centre of a building's largest outline ring — the anchor for its name label.
function buildingCentroid(b) {
  let best = null;
  for (const ring of b.outlines || []) {
    if (!ring || ring.length < 3) continue;
    if (!best || ring.length > best.length) best = ring;
  }
  if (!best) return null;
  let lat = 0, lng = 0;
  for (const p of best) { lat += p.lat; lng += p.lng; }
  return { lat: lat / best.length, lng: lng / best.length };
}
// A building name label anchored on the map (OverlayView, non-interactive). White
// text with a gray outline; opacity is set from outside so it can fade on zoom.
function createBuildingLabel(map, position, name) {
  const gmaps = window.google.maps;
  const latLng = new gmaps.LatLng(position.lat, position.lng);
  let el = null;
  class Label extends gmaps.OverlayView {
    // floatPane is the topmost overlay pane, so the name paints ABOVE the
    // polygons, the route polylines and the marker icons. (.wd-blabel is
    // pointer-events:none, so sitting on top never blocks a tap.)
    onAdd() { el = document.createElement('div'); el.className = 'wd-blabel'; el.textContent = name; this.getPanes().floatPane.appendChild(el); }
    draw() { const pt = this.getProjection()?.fromLatLngToDivPixel(latLng); if (pt && el) { el.style.left = `${pt.x}px`; el.style.top = `${pt.y}px`; } }
    onRemove() { el?.remove(); el = null; }
  }
  const o = new Label(); o.setMap(map);
  return { setOpacity: (v) => { if (el) { el.style.opacity = String(v); el.style.display = v <= 0.01 ? 'none' : 'block'; } }, remove: () => o.setMap(null) };
}
// White building names: fully opaque at/below `top` zoom, gone at/above `bottom`,
// linearly fading between — so they read on the campus overview and clear out as
// the detailed floor view takes over.
const LABEL_FADE = { top: 16, bottom: 18.5 };
const labelOpacity = (z) => {
  if (z == null || z <= LABEL_FADE.top) return 1;
  if (z >= LABEL_FADE.bottom) return 0;
  return (LABEL_FADE.bottom - z) / (LABEL_FADE.bottom - LABEL_FADE.top);
};

// University of Manitoba brand palette (from um-brand-guidelines-um-logo.pdf).
const UM = { brown: '#431a0d', blue: '#0060a8', sky: '#00a8e4', gold: '#fcb40c' };

// Smallest signed difference between two compass headings, in degrees.
const headingDelta = (a, b) => Math.abs(((a - b + 540) % 360) - 180);

function haversine(a, b) {
  const R = 6371000, toR = x => (x * Math.PI) / 180;
  const dLat = toR(b.lat - a.lat), dLng = toR(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toR(a.lat)) * Math.cos(toR(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}
const fmtDist = (m) => (m == null ? null : m < 950 ? `${Math.round(m / 10) * 10} m` : `${(m / 1000).toFixed(1)} km`);

// Project `pt` onto the polyline `pts` (each {lat,lng,floor}). Returns the foot
// of the perpendicular (the nearest point on the line), the index of the vertex
// that STARTS the nearest segment, and the perpendicular distance in metres — so
// the drawn route can be trimmed to what's still AHEAD of the walker, and a large
// distance flags that they've strayed and the path should be re-solved. Uses a
// local equirectangular frame centred on `pt` (accurate at campus scale).
function projectOnPath(pt, pts) {
  if (!pts || !pts.length) return null;
  if (pts.length === 1) return { index: 0, dist: haversine(pt, pts[0]), point: { ...pts[0] } };
  const mPerDegLat = 111320, mPerDegLng = 111320 * Math.cos((pt.lat * Math.PI) / 180);
  const X = p => ({ x: (p.lng - pt.lng) * mPerDegLng, y: (p.lat - pt.lat) * mPerDegLat });
  let best = null;
  for (let i = 0; i < pts.length - 1; i++) {
    const a = X(pts[i]), b = X(pts[i + 1]);
    const dx = b.x - a.x, dy = b.y - a.y, len2 = dx * dx + dy * dy;
    let t = len2 > 0 ? -(a.x * dx + a.y * dy) / len2 : 0; // param of the foot from the origin (= pt)
    t = Math.max(0, Math.min(1, t));
    const d = Math.hypot(a.x + t * dx, a.y + t * dy);
    if (!best || d < best.dist) best = {
      index: i, dist: d,
      point: {
        lat: pts[i].lat + (pts[i + 1].lat - pts[i].lat) * t,
        lng: pts[i].lng + (pts[i + 1].lng - pts[i].lng) * t,
        floor: pts[i].floor,
      },
    };
  }
  return best;
}
const nowMinutes = () => { const d = new Date(); return d.getHours() * 60 + d.getMinutes(); };

// "in 1h 05m 12s" / "in 25m 30s" / "in 45s" / "Happening now" from HH:MM today.
function countdownText(time, tick) { // tick just forces recompute
  const m = /^(\d{1,2}):(\d{2})/.exec(String(time || '').trim());
  if (!m) return null;
  const target = new Date(); target.setHours(Number(m[1]), Number(m[2]), 0, 0);
  const s = Math.round((target - Date.now()) / 1000);
  if (s <= 0 && s > -3600) return 'Happening now';
  if (s <= -3600) return 'Earlier today';
  const pad = n => String(n).padStart(2, '0');
  const h = Math.floor(s / 3600), mm = Math.floor((s % 3600) / 60), ss = s % 60;
  if (h > 0) return `in ${h}h ${pad(mm)}m ${pad(ss)}s`;
  if (mm > 0) return `in ${mm}m ${pad(ss)}s`;
  return `in ${ss}s`;
}

// Fisher-Yates shuffle (copy).
function shuffle(arr) { const a = [...arr]; for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a; }

// Route from `fromId` through an ordered list of node ids (checkpoints then the
// destination), chaining findRoute and merging the segments into one route.
function routeThrough(md, fromId, waypoints, opts) {
  const all = { nodes: [], transitions: [], legs: [] };
  let cur = fromId;
  for (const wp of waypoints) {
    if (!wp || wp === cur) continue;
    const r = findRoute(md, cur, wp, opts);
    if (!r?.nodes?.length) return null;
    const ns = r.nodes;
    if (all.nodes.length && ns[0]?.id === all.nodes[all.nodes.length - 1].id) all.nodes.push(...ns.slice(1));
    else all.nodes.push(...ns);
    all.transitions.push(...(r.transitions || []));
    all.legs.push(...(r.legs || []));
    cur = wp;
  }
  return all.nodes.length ? all : null;
}

// Bathroom type → friendly label + brand colour (men's blue, women's light red,
// universal purple) for the "closest bathrooms" popup and the map highlight.
const BATH_LABELS = { mens: "Men's", womens: "Women's", universal: 'Universal' };
const BATH_COLORS = { mens: '#0060a8', womens: '#e5556a', universal: '#8e24aa' };

// Resolve a bathroom PLACE to a routable target: match it to a room in the
// pathfinder graph (building + room number) for its door node(s) and floor,
// falling back to the nearest graph node to its pin when there's no room match.
function resolveBathroom(md, p) {
  let nodeIds = null, floor = null, buildingName = '', lat = p.lat, lng = p.lng;
  if (md) {
    const r = p.roomNumber && md.roomIndex.find(x =>
      x.building === p.building && String(x.roomNumber).toLowerCase() === String(p.roomNumber).toLowerCase());
    if (r) {
      nodeIds = r.nodeIds?.length ? r.nodeIds : [r.nodeId];
      const n = md.nodes.get(r.nodeId);
      floor = n?.floor ?? null; buildingName = r.buildingName;
      if (lat == null && n) { lat = n.lat; lng = n.lng; }
    } else if (lat != null) {
      const nn = nearestNode([...md.nodes.values()], { lat, lng });
      if (nn?.node) { nodeIds = [nn.node.id]; floor = nn.node.floor ?? null; buildingName = md.buildings.find(b => b.slug === nn.node.building)?.name || nn.node.building || ''; }
    }
  }
  if (!buildingName) buildingName = md?.buildings.find(b => b.slug === p.building)?.name || p.building || '';
  return { nodeIds, floor, buildingName, lat, lng };
}

// Angle (deg) of the turn at b when walking a→b→c, and which way it bends.
// lng is scaled by cos(lat) so the angle is measured in true ground geometry.
function turnInfo(a, b, c) {
  const k = Math.cos((b.lat * Math.PI) / 180);
  const v1 = { x: (b.lng - a.lng) * k, y: b.lat - a.lat };
  const v2 = { x: (c.lng - b.lng) * k, y: c.lat - b.lat };
  if (Math.hypot(v1.x, v1.y) < 1e-9 || Math.hypot(v2.x, v2.y) < 1e-9) return null;
  const cross = v1.x * v2.y - v1.y * v2.x;   // >0 ⇒ counter-clockwise ⇒ left
  const dot = v1.x * v2.x + v1.y * v2.y;
  const angle = (Math.atan2(Math.abs(cross), dot) * 180) / Math.PI;
  return { angle, dir: cross > 0 ? 'left' : 'right' };
}

// Turn a computed route into human step-by-step directions. Derives:
//  · Enter/Exit BUILDING at exit nodes (outside↔building boundaries)
//  · Take the stairs/elevator from Level X to Level Y (route.transitions)
//  · Enter the tunnels from X to Y (tunnel pseudo-building runs)
//  · Turn left/right inside BUILDING at sharp (≥35°) corners
const TURN_DEG = 35;   // a bend this sharp (at a junction) is announced as a turn
const WALK_MIN = 40;   // only call out a walking stretch once it's at least this long (m)
const DIR_ICON_ZOOM = 18; // map turn/entry icons appear at/above this zoom level
// Map-marker glyph + ring colour for the steps that get an on-map icon.
const DIR_MARKERS = {
  'turn-left':  { glyph: '↰', ring: '#0060a8' },
  'turn-right': { glyph: '↱', ring: '#0060a8' },
  enter:  { glyph: '🚪', ring: '#2e7d32' },
  exit:   { glyph: '🚪', ring: '#c62828' },
  tunnel: { glyph: '🚇', ring: '#5d4037' },
};
function buildDirections(route, md) {
  const nodes = route?.nodes || [];
  if (nodes.length < 2) return [];
  const isOutside = (n) => !n.building || n.building === 'outdoors' || n.floor === 0;
  const nameOf = (slug) => {
    if (!slug || slug === 'outdoors') return 'outside';
    if (slug === 'tunnels') return 'the tunnels';
    return md?.buildings?.find(x => x.slug === slug)?.name || slug;
  };
  const levelText = (f) => (dispFloor(f) >= 1 ? `Level ${dispFloor(f) * 100}` : 'outside');   // inline "(Level 100)"
  const levelChip = (f) => (dispFloor(f) >= 1 ? `L${dispFloor(f) * 100}` : 'Outside');          // right-side chip
  // Shaft floor-changes are edges; index them both directions by node-id pair.
  const trans = new Map();
  for (const t of route?.transitions || []) {
    if (t.fromNode?.id && t.toNode?.id) {
      trans.set(`${t.fromNode.id}>${t.toNode.id}`, t);
      trans.set(`${t.toNode.id}>${t.fromNode.id}`, t);
    }
  }
  const steps = [];
  // Enter/Exit/tunnel callouts carry the floor inline (chip = null); every other
  // step shows the level they should be on as a far-right "LX00" chip.
  const push = (icon, text, chip = null, meta = null) => {
    const last = steps[steps.length - 1];
    if (!last || last.text !== text) steps.push({ id: steps.length, icon, text, chip, ...(meta || {}) });
  };
  const at = (n) => ({ lat: n.lat, lng: n.lng }); // marker position for a node
  // Straight-line distance accumulates between instructions and is flushed as a
  // "Walk Xm" step once it's long enough — so directions read "walk 500 m, then…".
  let walk = 0, walkFloor = null;
  const flushWalk = () => {
    if (walk >= WALK_MIN && dispFloor(walkFloor) >= 1) push('🚶', `Walk ${fmtDist(walk)}`, levelChip(walkFloor));
    walk = 0; walkFloor = null;
  };
  const degree = (n) => (md?.adjacency?.get(n.id)?.length || 0); // graph connections (a junction ⇒ ≥3)

  for (let i = 1; i < nodes.length; i++) {
    const prev = nodes[i - 1], cur = nodes[i];
    const t = trans.get(`${prev.id}>${cur.id}`); // shaft floor-change edge (vertical, not walking)
    // Only step-by-step INDOORS: accumulate walking distance on indoor edges only.
    if (!t && !isOutside(prev) && !isOutside(cur)) { if (walkFloor == null) walkFloor = prev.floor; walk += haversine(prev, cur); }

    // Building boundary between prev → cur.
    if (prev.building !== cur.building) {
      if (cur.building === 'tunnels') {
        let j = i; while (j < nodes.length && nodes[j].building === 'tunnels') j++;
        const exitB = j < nodes.length ? nodes[j].building : null;
        flushWalk();
        push('🚇', `Enter the tunnels from ${nameOf(prev.building)} to ${nameOf(exitB)} (${levelText(cur.floor)})`,
          null, { kind: 'tunnel', at: at(cur), floor: dispFloor(cur.floor) });
      } else if (prev.building === 'tunnels') {
        /* arrival building already named at tunnel entry */
      } else if (isOutside(prev) && !isOutside(cur)) {
        flushWalk();
        push('🚪', `Enter ${nameOf(cur.building)} (${levelText(cur.floor)})`,
          null, { kind: 'enter', at: at(cur), floor: dispFloor(cur.floor) });
      } else if (!isOutside(prev) && isOutside(cur)) {
        flushWalk();
        push('🚪', `Exit ${nameOf(prev.building)} (${levelText(prev.floor)})`,
          null, { kind: 'exit', at: at(prev), floor: dispFloor(prev.floor) });
      } else {
        flushWalk();
        push('🚪', `Go from ${nameOf(prev.building)} into ${nameOf(cur.building)}`,
          levelChip(cur.floor), { kind: 'enter', at: at(cur), floor: dispFloor(cur.floor) });
      }
    }

    // Stair / elevator floor change on this edge — call out the direction (up/down).
    if (t) {
      flushWalk();
      const fromL = dispFloor(t.fromFloor) * 100, toL = dispFloor(t.toFloor) * 100;
      const dir = toL > fromL ? 'up' : 'down';
      const text = t.kind === 'elevator'
        ? `Take the elevator ${dir} to Level ${toL}`
        : `Take the stairs ${dir} to Level ${toL}`;
      push(t.kind === 'elevator' ? '🛗' : '🪜', text, `L${toL}`,
        { kind: t.kind, at: at(t.toNode), floor: dispFloor(t.toFloor), nodeId: t.toNode.id });
      continue; // don't also report a turn at a shaft node
    }

    // Turns are announced at INTERSECTION nodes (the authored decision points)
    // and at fallback crossroads (degree ≥ 3 bending ≥ TURN_DEG). Only TURNS are
    // called out — a straight pass through an intersection gets no step.
    if (i < nodes.length - 1 && !isOutside(cur)) {
      const ti = turnInfo(prev, cur, nodes[i + 1]);
      const isX = cur.type === 'intersection';
      const isTurn = ti && ti.angle >= TURN_DEG;
      if (isTurn && (isX || degree(cur) >= 3)) {
        // Fold the walk up to this turn into the instruction — "After 40 m, turn
        // left …" — so the user knows how far to go before turning. Consumes the
        // pending walk instead of emitting a separate "Walk" step.
        const pending = dispFloor(walkFloor) >= 1 ? walk : 0;
        const after = pending >= 8 ? `After ${fmtDist(pending)}, ` : '';
        walk = 0; walkFloor = null;
        const inside = cur.building !== 'tunnels' ? ` inside ${nameOf(cur.building)}` : '';
        push(ti.dir === 'left' ? '↰' : '↱', `${after}${after ? 'turn' : 'Turn'} ${ti.dir}${isX ? ' at the intersection' : inside}`, levelChip(cur.floor),
          { kind: ti.dir === 'left' ? 'turn-left' : 'turn-right', at: at(cur), floor: dispFloor(cur.floor), intersection: isX, nodeId: cur.id });
      }
    }
  }
  flushWalk();
  const last = nodes[nodes.length - 1];
  if (!isOutside(last) && last.building !== 'tunnels') {
    push('🏁', `Arrive at ${nameOf(last.building)}${last.room_number ? ` ${last.room_number}` : ''}`, levelChip(last.floor),
      { kind: 'arrive', at: at(last), floor: dispFloor(last.floor), nodeId: last.id });
  }
  return steps;
}

export default function WelcomeDay({ dataSource = null, apiKey: apiKeyProp = null }) {
  const apiKey = apiKeyProp || process.env.REACT_APP_MAPS_KEY || '';
  const { isLoaded, loadError } = useJsApiLoader({ googleMapsApiKey: apiKey });

  const [data, setData] = useState(null);
  const [focusedId, setFocusedId] = useState(null); // detail sheet
  const [listOpen, setListOpen] = useState(false);
  const [backpackOpen, setBackpackOpen] = useState(false); // resources backpack modal
  const [userPos, setUserPos] = useState(null);
  const [gpsAcc, setGpsAcc] = useState(null); // GPS accuracy (m); null = no fix yet / fallback

  const [tick, setTick] = useState(0); // 1s heartbeat for the countdown
  const [helpOpen, setHelpOpen] = useState(false);
  const [welcomeOpen, setWelcomeOpen] = useState(false); // first-launch "how rooms work" graphic
  const [dirHintOpen, setDirHintOpen] = useState(false); // "tap a direction" pointer (first open)
  const [bpSeen, setBpSeen] = useState(() => !!loadHints().backpack); // backpack opened at least once
  const [bpHintOpen, setBpHintOpen] = useState(false); // "these are resources" pointer (first open)
  const [route, setRoute] = useState(null);        // pathfinder route to the next event
  const [dirOpen, setDirOpen] = useState(false);   // step-by-step directions dropdown
  const [highlightStep, setHighlightStep] = useState(null); // step flashed after tapping its map icon
  const [activeDir, setActiveDir] = useState(0);   // which step is the current "main direction" (GPS-advanced)
  const [places, setPlaces] = useState([]);        // campus places (we surface bathrooms)
  const [bathOpen, setBathOpen] = useState(false); // "closest bathrooms" popup
  const [bathFilter, setBathFilter] = useState(null); // null (all) | mens | womens | accessible
  const [bathTarget, setBathTarget] = useState(null); // temporary pathfinding override → a bathroom
  const bathTargetRef = useRef(null); useEffect(() => { bathTargetRef.current = bathTarget; }, [bathTarget]);
  const [bathGoing, setBathGoing] = useState(false); // committed to guiding (vs. just previewing the route)
  const [focusBath, setFocusBath] = useState(null); // bathroom whose room polygon is highlighted
  const [selectedFloor, setSelectedFloor] = useState(0); // floor shown on the map (0 = outside)
  const [zoom, setZoom] = useState(16);
  const zoomRef = useRef(16); useEffect(() => { zoomRef.current = zoom; }, [zoom]);
  const iconMarkersRef = useRef([]);               // stair/elevator glyph markers (resized on zoom)

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const phoneRef = useRef(null);       // root positioning context (exposes --wd-island-h)
  const islandBarRef = useRef(null);   // measured so side controls sit BELOW the next-up bar
  const [follow, setFollow] = useState(false); // compass-follow: recenter + rotate map to heading
  const markerObjsRef = useRef(new Map());
  const mapDataRef = useRef(null);       // pathfinder graph
  const userMarkerRef = useRef(null);
  const routeSegsRef = useRef([]); // per-floor route polylines: [{ poly, floor }]
  const plannedRef = useRef(null); // solved route held between GPS updates: { destKey, avoid, pts, dest }
  const dirMarkersRef = useRef([]); // turn/entry icons along the route: [{ marker, floor }]

  const outdoorNodesRef = useRef([]);   // outdoor nav nodes (route start candidates)
  const reachableRef = useRef(null);    // node ids reachable from the outdoor network
  const [graphReady, setGraphReady] = useState(false);
  const [assign, setAssign] = useState({}); // eventId → { nodeId, lat, lng, label }

  useEffect(() => { loadWelcomeDay().then(setData); }, []);
  useEffect(() => { loadPlaces().then(setPlaces).catch(() => {}); }, []);
  const events = useMemo(() => sortedEvents(data?.events || []), [data]);

  // Bathrooms (a place type), each resolved to a routable node + floor and sorted
  // by walking distance from the user — feeds the "closest bathrooms" popup.
  const bathrooms = useMemo(() => {
    const md = mapDataRef.current;
    const list = [];
    for (const p of places) {
      if (p.type !== 'bathroom') continue;
      const r = resolveBathroom(md, p);
      if (!(r.nodeIds?.length) && r.lat == null) continue; // nowhere to route/pan to
      const dist = (userPos && r.lat != null) ? haversine(userPos, { lat: r.lat, lng: r.lng }) : null;
      list.push({
        id: p.id, name: p.name, building: r.buildingName, slug: p.building, floor: r.floor,
        roomNumber: p.roomNumber, type: p.bathroomType, accessible: p.accessible, periodSupply: p.periodSupply,
        nodeIds: r.nodeIds, lat: r.lat, lng: r.lng, dist,
      });
    }
    list.sort((a, b) => (a.dist ?? Infinity) - (b.dist ?? Infinity));
    return list;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [places, userPos, graphReady]);

  // Filter tabs: Men's / Women's / Accessible. Universal counts as BOTH men's and
  // women's, so it appears under either gendered tab.
  const shownBathrooms = useMemo(() => bathrooms.filter(b => {
    if (!bathFilter) return true; // no filter → show all
    if (bathFilter === 'accessible') return b.accessible;
    if (bathFilter === 'womens') return b.type === 'womens' || b.type === 'universal';
    return b.type === 'mens' || b.type === 'universal'; // 'mens'
  }), [bathrooms, bathFilter]);

  // Resolve each event's location. The editor can set it two ways — a destination
  // room (routable door node) or a map-placed pin (explicit lat/lng) — and either
  // is honoured exactly. Only when an event has NO location set do we fall back to
  // a random connected room (see the assignment effect below).
  const displayEvents = useMemo(() => events.map(e => {
    const md = mapDataRef.current;
    const dn = e.destNodeId && md?.nodes.get(e.destNodeId);
    if (dn) return { ...e, lat: dn.lat, lng: dn.lng, floor: dn.floor ?? null, slug: dn.building, roomNumber: dn.room_number || null, location: e.location || `${dn.building} ${dn.room_number || ''}`.trim() };
    if (e.lat != null && e.lng != null) return e;   // explicit editor coordinates — use as-is
    const a = assign[e.id];
    return a ? { ...e, lat: a.lat, lng: a.lng, floor: a.floor ?? null, slug: a.slug ?? null, roomNumber: a.roomNumber ?? null, location: a.label } : e;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [events, assign, graphReady]);

  // Floors the route passes through (in order; 0 = outside) and the buildings it
  // enters — drives the floor switcher + which structure polygons to draw.
  const routeFloors = useMemo(() => {
    const seen = new Set(), out = [];
    for (const n of route?.nodes || []) { const f = dispFloor(n.floor); if (!seen.has(f)) { seen.add(f); out.push(f); } }
    return out;
  }, [route]);
  const routeBuildings = useMemo(() => {
    const s = new Set();
    for (const n of route?.nodes || []) if (n.building && n.building !== 'outdoors' && n.building !== 'tunnels') s.add(n.building);
    return s;
  }, [route]);

  // Step-by-step directions derived from the route (Enter/Exit buildings, shaft
  // level changes, tunnel entries, sharp turns). Feeds the directions dropdown.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const directions = useMemo(() => buildDirections(route, mapDataRef.current), [route, graphReady]);

  // Advance the collapsed "main direction" as the walker passes each step. The
  // active step is the next maneuver still ahead of them along the route. It only
  // moves FORWARD and is frozen while GPS is weak (a bad fix can't skip a step).
  const activeDirRef = useRef(0);
  useEffect(() => { activeDirRef.current = 0; setActiveDir(0); }, [route]); // reset on a fresh route
  useEffect(() => {
    const weakFix = gpsAcc == null || gpsAcc > GPS_WEAK_M; // inaccurate GPS ⇒ freeze the lead
    if (weakFix || !userPos || !directions.length) return;
    const pts = plannedRef.current?.pts;
    if (!pts || pts.length < 2) return;
    const cum = [0];
    for (let i = 1; i < pts.length; i++) cum[i] = cum[i - 1] + haversine(pts[i - 1], pts[i]);
    const along = (t) => { const pr = projectOnPath(t, pts); return pr ? cum[pr.index] + haversine(pts[pr.index], pr.point) : 0; };
    const userAlong = along(userPos);
    let idx = directions.length - 1;                 // default: past everything ⇒ arrival
    for (let i = 0; i < directions.length; i++) {
      const d = directions[i];
      if (!d.at) continue;
      if (along(d.at) >= userAlong - 6) { idx = i; break; } // first maneuver at/ahead of me
    }
    if (idx > activeDirRef.current) { activeDirRef.current = idx; setActiveDir(idx); }
  }, [userPos, directions, gpsAcc]); // eslint-disable-line react-hooks/exhaustive-deps

  // Glide the camera to a point: panTo already animates the pan; step the zoom in
  // one level at a time (rather than a single setZoom jump) so the whole move
  // reads as a smooth flight, not a snap.
  const zoomTimerRef = useRef(null);
  const glideTo = useCallback((target, wantZoom) => {
    const map = mapInstanceRef.current;
    if (!map || !target) return;
    map.panTo(target);
    if (zoomTimerRef.current) { clearInterval(zoomTimerRef.current); zoomTimerRef.current = null; }
    const want = Math.max(map.getZoom() || 0, wantZoom);
    if ((map.getZoom() || 0) >= want) return;
    zoomTimerRef.current = setInterval(() => {
      const z = Math.min((map.getZoom() || 0) + 1, want);
      map.setZoom(z);
      if (z >= want) { clearInterval(zoomTimerRef.current); zoomTimerRef.current = null; }
    }, 150);
  }, []);
  useEffect(() => () => { if (zoomTimerRef.current) clearInterval(zoomTimerRef.current); }, []);

  // Tap a direction step (turn/entry/shaft/arrival) to focus the map on it:
  // switch to that step's floor, then glide (not snap) to it.
  const handleDirClick = useCallback((step) => {
    if (!step?.at) return;
    if (step.floor != null) setSelectedFloor(step.floor);
    glideTo(step.at, 19);
  }, [glideTo]);

  // Guide-box presentation for the active override (a bathroom, a resource, or a
  // manually chosen event).
  const guideView = useMemo(() => {
    if (!bathTarget) return null;
    const kind = bathTarget.kind;
    const level = bathTarget.floor != null ? `Level ${dispFloor(bathTarget.floor) * 100}` : null;
    if (kind === 'resource' || kind === 'event') {
      return {
        color: kind === 'event' ? UM.blue : UM.gold,
        icon: bathTarget.icon || (kind === 'event' ? '📍' : '🎒'),
        title: bathTarget.name || (kind === 'event' ? 'Event' : 'Resource'),
        sub: [bathTarget.building, level].filter(Boolean).join(' · '),
      };
    }
    return {
      color: BATH_COLORS[bathTarget.type] || '#455a64',
      icon: '🚻',
      title: 'Guiding to bathroom',
      sub: [BATH_LABELS[bathTarget.type], bathTarget.building, level].filter(Boolean).join(' · '),
    };
  }, [bathTarget]);

  // 1-second heartbeat for the live countdown.
  useEffect(() => { const t = setInterval(() => setTick(x => x + 1), 1000); return () => clearInterval(t); }, []);

  // Load the pathfinding graph once; precompute outdoor nodes for routing.
  useEffect(() => {
    if (!dataSource) return;
    let cancelled = false;
    loadAllMapFiles(dataSource).then(files => {
      if (cancelled) return;
      const md = buildMapData(files);
      mapDataRef.current = md;
      const all = [...md.nodes.values()];
      const outdoor = all.filter(n => n.building === 'outdoors' || n.floor === 0);
      outdoorNodesRef.current = outdoor.length ? outdoor : all;
      // BFS the graph from the outdoor network so we only ever target rooms that
      // are actually reachable on foot from outside.
      const reachable = new Set();
      const q = (outdoor.length ? outdoor : all).map(n => n.id);
      q.forEach(id => reachable.add(id));
      for (let i = 0; i < q.length; i++) {
        for (const { other } of md.adjacency.get(q[i]) || []) {
          if (!reachable.has(other)) { reachable.add(other); q.push(other); }
        }
      }
      reachableRef.current = reachable;
      setGraphReady(true);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [dataSource]);

  // Give a random connected room ONLY to events with no location set in the
  // editor (no destination room and no map-placed pin). Events that already have
  // a location are left untouched — they use exactly what the editor gave them.
  useEffect(() => {
    const md = mapDataRef.current;
    if (!graphReady || !md || !events.length) return;
    const needs = events.filter(e => !e.destNodeId && e.lat == null);
    if (!needs.length || needs.every(e => assign[e.id])) return;
    const reach = reachableRef.current;
    const connected = [];
    for (const r of md.roomIndex) {
      if (!r.roomNumber) continue;
      const node = md.nodes.get(r.nodeId);
      if (!node || node.lat == null) continue;
      if (node.building === 'outdoors' || node.building === 'tunnels') continue; // want indoor rooms
      if (!(md.adjacency.get(r.nodeId) || []).length) continue;                   // must connect to wayfinding nodes
      if (reach && reach.size && !reach.has(r.nodeId)) continue;                   // must be reachable from outside
      connected.push({ nodeId: r.nodeId, lat: node.lat, lng: node.lng, floor: node.floor ?? 1, slug: node.building, roomNumber: r.roomNumber, label: `${r.buildingName} ${r.roomNumber}` });
    }
    if (!connected.length) return;
    const pool = shuffle(connected);
    const next = { ...assign };
    needs.forEach((e, i) => { if (!next[e.id]) next[e.id] = pool[i % pool.length]; });
    setAssign(next);
  }, [graphReady, events, assign]);

  // The "next up" event = first upcoming by clock, else the first of the day.
  const nextEvent = useMemo(() => {
    if (!displayEvents.length) return null;
    const now = nowMinutes();
    return displayEvents.find(e => timeToMinutes(e.time) >= now) || displayEvents[0];
  }, [displayEvents]);

  const focused = useMemo(() => displayEvents.find(e => e.id === focusedId) || null, [displayEvents, focusedId]);

  // Geolocation → distance + routing. Only update on a SIGNIFICANT move so the
  // route (and its direction arrows) refresh on real movement, not GPS jitter.
  // If geolocation is denied/unavailable (common on desktop or non-HTTPS), fall
  // back to a demo location off campus so a path is still shown.
  const lastPosRef = useRef(null);
  const [locatedUser, setLocatedUser] = useState(false); // a real GPS fix arrived (not the demo fallback)
  const set = np => { lastPosRef.current = np; setUserPos(np); };
  useEffect(() => {
    let got = false;
    const fallback = setTimeout(() => { if (!got) { set(DEMO_POS); setGpsAcc(null); } }, 4000);
    if (!navigator.geolocation) { clearTimeout(fallback); set(DEMO_POS); setGpsAcc(null); return; }
    const id = navigator.geolocation.watchPosition(p => {
      got = true;
      setLocatedUser(true); // permission granted + real fix ⇒ jump the map to the user
      setGpsAcc(p.coords.accuracy ?? null); // metres; every reading, even if we don't move
      const np = { lat: p.coords.latitude, lng: p.coords.longitude };
      if (!lastPosRef.current || haversine(lastPosRef.current, np) >= MOVE_THRESHOLD_M) set(np);
    }, () => { if (!got) { set(DEMO_POS); setGpsAcc(null); } }, { enableHighAccuracy: true, timeout: 8000 });
    return () => { clearTimeout(fallback); navigator.geolocation.clearWatch(id); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // When location permission is accepted (first real fix), move the map to the
  // user, once. Runs after the initial fit-to-events so it takes precedence.
  const centeredOnUserRef = useRef(false);
  useEffect(() => {
    if (!mapReady || centeredOnUserRef.current || !locatedUser || !userPos) return;
    centeredOnUserRef.current = true;
    const map = mapInstanceRef.current;
    map.panTo(userPos);
    map.setZoom(Math.max(map.getZoom() || 0, 17));
  }, [mapReady, locatedUser, userPos]);

  // First-ever launch: show the "how room numbers work" graphic.
  useEffect(() => { if (!loadHints().welcome) setWelcomeOpen(true); }, []);
  const closeWelcome = () => { setWelcomeOpen(false); markHint('welcome'); };
  // Open/close the directions box; the first open also fires the "tap a step" hint.
  const toggleDir = () => {
    setDirOpen(o => !o);
    if (!loadHints().dirtap) { setDirHintOpen(true); markHint('dirtap'); }
  };
  const islandDist = (userPos && nextEvent?.lat != null) ? fmtDist(haversine(userPos, nextEvent)) : null;
  // Weak/loading GPS (e.g. indoors or in a tunnel): warn the location may be off.
  const gpsWeak = gpsAcc == null || gpsAcc > GPS_WEAK_M;

  // Map bootstrap (satellite).
  useEffect(() => {
    if (!isLoaded || !mapRef.current || mapInstanceRef.current) return;
    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      center: CENTER, zoom: 16, mapTypeId: 'satellite',
      tilt: 0, heading: 0, keyboardShortcuts: false, ...orientationOptions(),
      mapTypeControl: false, streetViewControl: false, fullscreenControl: false, zoomControl: false,
      clickableIcons: false, gestureHandling: 'greedy',
    });
    mapInstanceRef.current.addListener('click', () => { setFocusedId(null); setListOpen(false); setBathOpen(false); });
    mapInstanceRef.current.addListener('zoom_changed', () => setZoom(mapInstanceRef.current.getZoom() || 16));
    setZoom(mapInstanceRef.current.getZoom() || 16);
    setMapReady(true);
  }, [isLoaded]);

  // Keep the right-side floating controls positioned RELATIVE to the next-up bar:
  // measure its height and expose it as --wd-island-h so the backpack / bathroom
  // buttons always sit below it, however far the title/meta wrap on small phones.
  useLayoutEffect(() => {
    const bar = islandBarRef.current, phone = phoneRef.current;
    if (!bar || !phone) return;
    const apply = () => phone.style.setProperty('--wd-island-h', `${Math.round(bar.getBoundingClientRect().height)}px`);
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(bar);
    return () => ro.disconnect();
  }, []);

  // ── Compass-follow mode ────────────────────────────────────────────────────
  // Recenter on the user and rotate the map to their heading (forward = up).
  // Rotation needs a VECTOR map (REACT_APP_MAPS_ID); without it we still center.
  const followRef = useRef(false); useEffect(() => { followRef.current = follow; }, [follow]);
  const userPosRefEarly = useRef(userPos); useEffect(() => { userPosRefEarly.current = userPos; }, [userPos]);

  // Center on the user whenever their position updates while following.
  useEffect(() => {
    if (!follow || !userPos || !mapInstanceRef.current) return;
    mapInstanceRef.current.panTo(userPos);
  }, [follow, userPos]);

  // While following, rotate the map to the user's compass heading (forward = up).
  // Only runs if the device streams a heading (permission granted) — otherwise
  // the map just stays centred and north-up, no direction indicator.
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!follow || !map) return;
    if (userPosRefEarly.current) map.panTo(userPosRefEarly.current);
    let lastLocked = null;
    const apply = (heading) => {
      if (heading == null || Number.isNaN(heading) || !followRef.current) return;
      const m = mapInstanceRef.current;
      if (!m) return;
      // Lock the map looking the user's way (skip sub-2° jitter to avoid churn).
      if (lastLocked == null || headingDelta(heading, lastLocked) >= 2) {
        lastLocked = heading;
        try { m.setHeading(heading); m.setTilt(0); } catch { /* raster map — cannot rotate */ }
      }
    };
    // iOS streams a true compass heading on `deviceorientation`; Android streams
    // an absolute (true-north) heading on `deviceorientationabsolute`. Listen to
    // both and only trust `alpha` when the event is flagged absolute, so the map
    // keeps turning live instead of freezing after the first reading.
    const onOrient = (e) => apply(
      typeof e.webkitCompassHeading === 'number' ? e.webkitCompassHeading
        : (e.absolute && typeof e.alpha === 'number') ? 360 - e.alpha : null
    );
    const hasAbsolute = 'ondeviceorientationabsolute' in window;
    window.addEventListener('deviceorientation', onOrient, true);
    if (hasAbsolute) window.addEventListener('deviceorientationabsolute', onOrient, true);
    return () => {
      window.removeEventListener('deviceorientation', onOrient, true);
      if (hasAbsolute) window.removeEventListener('deviceorientationabsolute', onOrient, true);
    };
  }, [follow]);

  // A manual drag means "I'm looking around" — drop out of follow so the map
  // stops fighting the user. (Our own panTo() doesn't fire dragstart.)
  useEffect(() => {
    if (!mapReady) return;
    const map = mapInstanceRef.current;
    const l = map.addListener('dragstart', () => { if (followRef.current) setFollow(false); });
    return () => l.remove();
  }, [mapReady]);

  const toggleFollow = useCallback(async () => {
    // iOS 13+ gates device orientation behind a permission prompt that must be
    // requested from a user gesture (this click). Center regardless of the answer.
    try {
      const DOE = window.DeviceOrientationEvent;
      if (DOE && typeof DOE.requestPermission === 'function') await DOE.requestPermission();
    } catch { /* denied / unsupported — centering still works */ }
    setFollow((f) => {
      const next = !f;
      const map = mapInstanceRef.current;
      if (map) {
        if (next && userPosRefEarly.current) map.panTo(userPosRefEarly.current);
        if (!next) { try { map.setHeading(0); map.setTilt(0); } catch { /* raster */ } } // reset to north
      }
      return next;
    });
  }, []);

  const fitRef = useRef(false);
  useEffect(() => {
    if (!mapReady || fitRef.current || !displayEvents.length) return;
    if (dataSource && !Object.keys(assign).length) return; // wait for placeholder rooms first
    const b = new window.google.maps.LatLngBounds();
    let any = false;
    for (const e of displayEvents) if (e.lat != null) { b.extend({ lat: e.lat, lng: e.lng }); any = true; }
    if (any) { mapInstanceRef.current.fitBounds(b, { top: 130, bottom: 120, left: 40, right: 40 }); fitRef.current = true; }
  }, [mapReady, displayEvents, assign, dataSource]);

  // Pins — pulsating numbered stars (rebuilt on any focus/next/order change).
  useEffect(() => {
    if (!mapReady) return;
    const map = mapInstanceRef.current;
    const objs = markerObjsRef.current;
    for (const o of objs.values()) o.remove();
    objs.clear();
    displayEvents.forEach((ev, i) => {
      if (ev.lat == null) return;
      const isFocus = ev.id === focusedId, isNext = ev.id === nextEvent?.id;
      // Show a level chip under the star when the event sits on an indoor level
      // other than the one currently shown, so its floor is clear at a glance.
      const evFloor = ev.floor != null ? dispFloor(ev.floor) : null;
      const levelChip = (evFloor != null && evFloor >= 1 && evFloor !== selectedFloor) ? `L${evFloor * 100}` : null;
      objs.set(ev.id, createStarOverlay(map, { lat: ev.lat, lng: ev.lng }, {
        number: i + 1,
        color: isNext ? UM.gold : isFocus ? UM.sky : UM.blue,
        big: isFocus || isNext,
        z: isFocus ? 600 : isNext ? 500 : 100 + i,
        delay: (i % 6) * 0.35,
        onClick: () => focus(ev.id),
        levelChip,
      }));
    });
    return () => { for (const o of objs.values()) o.remove(); objs.clear(); };
  }, [displayEvents, focusedId, nextEvent, mapReady, selectedFloor]); // eslint-disable-line react-hooks/exhaustive-deps

  // User location dot.
  useEffect(() => {
    if (!mapReady || !userPos) return;
    if (!userMarkerRef.current) {
      userMarkerRef.current = new window.google.maps.Marker({
        map: mapInstanceRef.current, zIndex: 700, clickable: false,
        icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 8, fillColor: UM.blue, fillOpacity: 1, strokeColor: '#fff', strokeWeight: 3 },
      });
    }
    userMarkerRef.current.setPosition(userPos);
  }, [userPos, mapReady]);

  // ── Building name labels (white, gray-outlined) shown on the overview ───────
  // ONLY for the buildings on the current route, so the map names just what's
  // relevant to this trip. Anchored at each building's centroid; they fade out as
  // you zoom in (see the zoom effect below) so the detailed floor view is clean.
  const labelObjsRef = useRef([]);
  useEffect(() => {
    if (!mapReady || !graphReady) return;
    const md = mapDataRef.current, map = mapInstanceRef.current;
    labelObjsRef.current.forEach(l => l.remove()); labelObjsRef.current = [];
    for (const b of md.buildings) {
      if (!b.name || !routeBuildings.has(b.slug)) continue; // route buildings only
      const c = buildingCentroid(b);
      if (c) labelObjsRef.current.push(createBuildingLabel(map, c, b.name));
    }
    const o = labelOpacity(zoomRef.current);
    labelObjsRef.current.forEach(l => l.setOpacity(o));
    return () => { labelObjsRef.current.forEach(l => l.remove()); labelObjsRef.current = []; };
  }, [mapReady, graphReady, routeBuildings]);

  // ── Beige structure polygons for the SELECTED floor ────────────────────────
  // Building footprints (all buildings) plus room polygons for the building(s)
  // the route enters, so you can see the indoor layout on that level.
  const structObjsRef = useRef([]);
  useEffect(() => {
    if (!mapReady || !graphReady) return;
    const md = mapDataRef.current, map = mapInstanceRef.current;
    structObjsRef.current.forEach(p => p.setMap(null));
    structObjsRef.current = [];
    iconMarkersRef.current = [];
    if (selectedFloor === 0) return; // outside — no indoor structure
    for (const b of md.buildings) {
      const inRoute = routeBuildings.has(b.slug);
      // Tunnels (floor -1) are shown on Level 1 so their corridors are clear.
      const polys = (b.polygonsByFloor?.get(selectedFloor) || [])
        .concat(selectedFloor === 1 ? (b.polygonsByFloor?.get(-1) || []) : []);
      for (const poly of polys) {
        if (!poly.ring?.length) continue;
        const outline = poly.type === 'outline';
        const tunnel = poly.type === 'tunnel';
        if (!outline && !tunnel && !inRoute) continue; // rooms only for route buildings
        const c = POLY_COLORS[poly.type] || POLY_COLORS.room;
        structObjsRef.current.push(new window.google.maps.Polygon({
          paths: poly.ring, map, clickable: false, zIndex: c.z,
          fillColor: c.fill, fillOpacity: c.fillOp, strokeColor: c.stroke, strokeOpacity: 1, strokeWeight: c.strokeW,
        }));
        // Centre a stair/elevator glyph so the shaft type is obvious.
        if (poly.type === 'stair' || poly.type === 'elevator') {
          const ctr = poly.ring.reduce((s, p) => ({ lat: s.lat + p.lat, lng: s.lng + p.lng }), { lat: 0, lng: 0 });
          const at = { lat: ctr.lat / poly.ring.length, lng: ctr.lng / poly.ring.length };
          const px = Math.round(24 * zoomScale(zoomRef.current));
          const mk = new window.google.maps.Marker({
            position: at, map, clickable: false, zIndex: 4, visible: (zoomRef.current || 16) >= SHAFT_ICON_ZOOM,
            icon: { url: svgUri(shaftSvg(poly.type)), scaledSize: new window.google.maps.Size(px, px), anchor: new window.google.maps.Point(px / 2, px / 2) },
          });
          structObjsRef.current.push(mk);
          iconMarkersRef.current.push({ marker: mk, url: svgUri(shaftSvg(poly.type)) });
        }
      }
    }
    return () => { structObjsRef.current.forEach(p => p.setMap(null)); structObjsRef.current = []; };
  }, [mapReady, graphReady, selectedFloor, routeBuildings]);

  // ── Highlight the focused/target bathroom's room polygon ────────────────────
  // Matches the bathroom to its building's room polygon on the current level and
  // draws a gently pulsing gold overlay on top of it, so the destination stands
  // out among the beige rooms. Only shows on that room's own floor.
  const bathHiRef = useRef([]);
  useEffect(() => {
    if (!mapReady || !graphReady) return;
    bathHiRef.current.forEach(p => p.setMap(null)); bathHiRef.current = [];
    const fb = focusBath;
    if (!fb || !fb.roomNumber) return;
    const floor = dispFloor(fb.floor);
    if (floor !== selectedFloor) return; // the room only exists on its own level
    const md = mapDataRef.current, map = mapInstanceRef.current;
    const bld = md.buildings.find(x => x.slug === fb.slug);
    if (!bld) return;
    const want = String(fb.roomNumber).toLowerCase();
    const col = fb.color || UM.gold;
    for (const poly of bld.polygonsByFloor?.get(floor) || []) {
      if (!poly.ring?.length || String(poly.roomNumber || '').toLowerCase() !== want) continue;
      bathHiRef.current.push(new window.google.maps.Polygon({
        paths: poly.ring, map, clickable: false, zIndex: 6,
        fillColor: col, fillOpacity: 0.4, strokeColor: col, strokeOpacity: 1, strokeWeight: 3,
      }));
    }
    let raf;
    if (bathHiRef.current.length) {
      const start = performance.now();
      const pulse = (t) => {
        const k = 0.28 + 0.18 * (0.5 + 0.5 * Math.sin((t - start) / 360));
        for (const p of bathHiRef.current) p.setOptions({ fillOpacity: k });
        raf = requestAnimationFrame(pulse);
      };
      raf = requestAnimationFrame(pulse);
    }
    return () => { if (raf) cancelAnimationFrame(raf); bathHiRef.current.forEach(p => p.setMap(null)); bathHiRef.current = []; };
  }, [mapReady, graphReady, focusBath, selectedFloor]);

  // ── Highlight each event's room when its own level is showing ───────────────
  // On the selected floor, paint the room polygon of any event that lives there
  // in a unique violet so it stands out from the beige rooms and gold bathrooms.
  const EVENT_ROOM_COLOR = '#7b1fa2';
  const eventHiRef = useRef([]);
  useEffect(() => {
    if (!mapReady || !graphReady) return;
    eventHiRef.current.forEach(p => p.setMap(null)); eventHiRef.current = [];
    if (selectedFloor < 1) return; // rooms only exist on real building levels
    const md = mapDataRef.current, map = mapInstanceRef.current;
    for (const ev of displayEvents) {
      if (!ev.slug || ev.roomNumber == null || ev.floor == null) continue;
      if (dispFloor(ev.floor) !== selectedFloor) continue;
      const bld = md.buildings.find(x => x.slug === ev.slug);
      if (!bld) continue;
      const want = String(ev.roomNumber).toLowerCase();
      for (const poly of bld.polygonsByFloor?.get(selectedFloor) || []) {
        if (!poly.ring?.length || String(poly.roomNumber || '').toLowerCase() !== want) continue;
        eventHiRef.current.push(new window.google.maps.Polygon({
          paths: poly.ring, map, clickable: false, zIndex: 6,
          fillColor: EVENT_ROOM_COLOR, fillOpacity: 0.34, strokeColor: EVENT_ROOM_COLOR, strokeOpacity: 1, strokeWeight: 3,
        }));
      }
    }
    return () => { eventHiRef.current.forEach(p => p.setMap(null)); eventHiRef.current = []; };
  }, [mapReady, graphReady, displayEvents, selectedFloor]);

  // Resize the shaft glyphs + switcher pills with zoom (clamped ≤19), and hide
  // both when zoomed out past the campus-overview threshold (stars only).
  useEffect(() => {
    const s = zoomScale(zoom);
    mapRef.current?.style.setProperty('--wd-shaft-scale', String(s));
    const showShafts = zoom >= SHAFT_ICON_ZOOM;
    mapRef.current?.classList.toggle('wd-hide-shafts', !showShafts); // hides the .wd-shaft switcher pills (map-pane children)
    const px = Math.round(24 * s);
    for (const { marker, url } of iconMarkersRef.current) {
      marker.setIcon({ url, scaledSize: new window.google.maps.Size(px, px), anchor: new window.google.maps.Point(px / 2, px / 2) });
      marker.setVisible(showShafts);
    }
    // Fade the white building-name labels as the view zooms in.
    const lo = labelOpacity(zoom);
    for (const l of labelObjsRef.current) l.setOpacity(lo);
  }, [zoom]);

  // ── Stair/elevator switchers at the route's shaft transitions ──────────────
  // On the floor I'm viewing, a tap advances to the shaft's other floor; on the
  // other floor, a "back" switcher returns. Tapping never moves the camera.
  const shaftOverlaysRef = useRef([]);
  useEffect(() => {
    if (!mapReady) return;
    shaftOverlaysRef.current.forEach(o => o.remove());
    shaftOverlaysRef.current = [];
    for (const t of route?.transitions || []) {
      const fromF = dispFloor(t.fromFloor), toF = dispFloor(t.toFloor);
      if (fromF === toF) continue; // no visible level change (e.g. tunnel↔L1)
      if (fromF === selectedFloor) {
        shaftOverlaysRef.current.push(createShaftOverlay(mapInstanceRef.current, t.fromNode, {
          kind: t.kind, label: `L${fromF * 100} › L${toF * 100}`, color: floorColor(toF), onTap: () => selectFloor(toF),
        }));
      } else if (toF === selectedFloor) {
        shaftOverlaysRef.current.push(createShaftOverlay(mapInstanceRef.current, t.toNode, {
          kind: t.kind, label: `L${toF * 100} › L${fromF * 100}`, back: true, color: floorColor(fromF), onTap: () => selectFloor(fromF),
        }));
      }
    }
    return () => { shaftOverlaysRef.current.forEach(o => o.remove()); shaftOverlaysRef.current = []; };
  }, [mapReady, route, selectedFloor]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Turn / entry icons dotted along the route ──────────────────────────────
  // Small badges at every announced turn, entry, exit and tunnel mouth. They
  // only show once you're zoomed in (≥ DIR_ICON_ZOOM) and on the floor you're
  // viewing (outdoor ones always), so the campus overview stays uncluttered.
  const syncDirMarkers = useCallback(() => {
    const show = (zoomRef.current || 16) >= DIR_ICON_ZOOM;
    const sf = selectedFloorRef.current;
    for (const { marker, floor } of dirMarkersRef.current) marker.setVisible(show && (floor === sf || floor === 0));
  }, []);
  useEffect(() => {
    if (!mapReady) return;
    dirMarkersRef.current.forEach(m => m.marker.setMap(null));
    dirMarkersRef.current = [];
    const map = mapInstanceRef.current;
    for (const d of directions) {
      const g = d.kind && DIR_MARKERS[d.kind];
      if (!g || !d.at || d.at.lat == null) continue;
      const mk = new window.google.maps.Marker({
        position: d.at, map, clickable: true, cursor: 'pointer', zIndex: 450, visible: false,
        title: d.text || 'Open this direction',
        icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 9, fillColor: '#fff', fillOpacity: 1, strokeColor: g.ring, strokeWeight: 2.5 },
        label: { text: g.glyph, fontSize: '12px', fontWeight: '700', color: g.ring },
      });
      // Tap a map icon → open the directions list and flash that step.
      mk.addListener('click', () => { setDirOpen(true); setHighlightStep(d.id); });
      dirMarkersRef.current.push({ marker: mk, floor: d.floor, id: d.id });
    }
    syncDirMarkers();
    return () => { dirMarkersRef.current.forEach(m => m.marker.setMap(null)); dirMarkersRef.current = []; };
  }, [mapReady, directions, syncDirMarkers]);

  // Scroll the flashed step into view once the list is open, then clear the flash.
  useEffect(() => {
    if (highlightStep == null || !dirOpen) return;
    const el = document.getElementById(`wd-dir-${highlightStep}`);
    if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    const t = setTimeout(() => setHighlightStep(null), 2200);
    return () => clearTimeout(t);
  }, [highlightStep, dirOpen, directions]);
  useEffect(() => { syncDirMarkers(); }, [zoom, selectedFloor, syncDirMarkers]); // toggle on zoom/floor, no rebuild

  // Default the shown floor to the FIRST indoor level the route enters — the first
  // floor the user actually steps onto (a floor-2 entrance shows L200) — so its
  // rooms load right away. Falls back to outside (0) when the route stays outdoors.
  useEffect(() => {
    const firstIndoor = routeFloors.find(f => f >= 1);
    setSelectedFloor(firstIndoor ?? 0);
  }, [routeFloors]);

  // ── Live pathfinding: from the nearest OUTDOOR node to me → the next event's
  // ROOM node. The line stays on screen at all times; direction arrows march
  // along it, and it re-routes whenever my location changes significantly.
  const userPosRef = useRef(userPos); useEffect(() => { userPosRef.current = userPos; }, [userPos]);
  const nextEventRef = useRef(nextEvent); useEffect(() => { nextEventRef.current = nextEvent; }, [nextEvent]);
  // "Avoid tunnels" is authored in the Welcome Day editor and honoured here.
  const avoidTunnelsRef = useRef(false); useEffect(() => { avoidTunnelsRef.current = !!data?.avoidTunnels; }, [data]);
  const assignRef = useRef(assign); useEffect(() => { assignRef.current = assign; }, [assign]);
  const selectedFloorRef = useRef(selectedFloor); useEffect(() => { selectedFloorRef.current = selectedFloor; }, [selectedFloor]);

  // Split a floor-tagged point list into consecutive same-floor runs.
  const toSegments = (pts) => {
    if (pts.length < 2) return [];
    const segs = []; let cur = { floor: pts[0].floor, path: [pts[0]] };
    for (let i = 1; i < pts.length; i++) {
      cur.path.push(pts[i]);
      if (pts[i].floor !== cur.floor) { segs.push(cur); cur = { floor: pts[i].floor, path: [pts[i]] }; }
    }
    segs.push(cur);
    return segs.filter(s => s.path.length >= 2);
  };
  // A segment is "prominent" when it's on the floor I'm viewing OR it's an
  // outdoor segment (outside paths are always visible). Prominent segments carry
  // the arrows; off-floor segments are dimmed but same width and only lightly
  // translucent.
  const arrowIcons = () => [{ icon: { path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 3.4, fillColor: '#fff', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 1 }, offset: '0px', repeat: '80px' }];
  const isProminent = (floor) => floor === 0 || floor === selectedFloorRef.current;
  const styleSeg = (s) => {
    const prominent = isProminent(s.floor);
    s.poly.setOptions({
      strokeColor: floorColor(s.floor), strokeOpacity: prominent ? 0.95 : 0.6, strokeWeight: 5,
      zIndex: prominent ? 400 : 350, icons: prominent ? arrowIcons() : [],
    });
  };
  const clearSegs = () => { routeSegsRef.current.forEach(s => s.poly.setMap(null)); routeSegsRef.current = []; };

  // Redraw the on-map polylines from a floor-tagged point list.
  const drawSegs = (pts) => {
    const segs = toSegments(pts);
    clearSegs();
    for (const seg of segs) {
      const poly = new window.google.maps.Polyline({ map: mapInstanceRef.current, path: seg.path, geodesic: true });
      const rec = { poly, floor: seg.floor };
      styleSeg(rec);
      routeSegsRef.current.push(rec);
    }
  };
  // Trim a planned route to what's still ahead of the walker: from their current
  // position, along the perpendicular foot on the path, then the remaining nodes.
  const drawTrimmed = (planned, up) => {
    if (!planned.pts.length) { // fallback straight line — just shrink toward dest
      drawSegs([{ lat: up.lat, lng: up.lng, floor: 0 }, { lat: planned.dest.lat, lng: planned.dest.lng, floor: selectedFloorRef.current }]);
      return;
    }
    const proj = projectOnPath(up, planned.pts);
    const ahead = [proj.point, ...planned.pts.slice(proj.index + 1)];
    drawSegs([{ lat: up.lat, lng: up.lng, floor: proj.point.floor ?? 0 }, ...ahead]);
  };

  useEffect(() => {
    if (!mapReady) return;
    const md = mapDataRef.current, up = userPosRef.current, ev = nextEventRef.current, bt = bathTargetRef.current;
    if (!up) return;                                             // need an origin
    if (!bt && (!ev || ev.lat == null)) return;                  // and a destination
    const avoid = avoidTunnelsRef.current;
    // Identity of the current destination + routing mode. Changing it forces a
    // fresh solve; otherwise we keep the solved path and only trim it.
    const destKey = bt
      ? `bath:${bt.id ?? ''}:${(bt.nodeIds || []).join(',')}`
      : `ev:${ev.id}:${ev.destNodeId || assignRef.current[ev.id]?.nodeId || ''}:${(ev.checkpoints || []).map(c => c.nodeId).join(',')}`;

    // Solve the path once (from where the walker is now) and remember its fixed
    // geometry so later GPS updates can trim instead of re-running pathfinding.
    const solve = () => {
      const opts = avoid ? { noTunnels: true } : undefined;
      let computed = null;
      if (bt && md && bt.nodeIds?.length) {
        // OVERRIDE: route to the chosen bathroom (multi-source over its doors).
        const from = nearestNode(outdoorNodesRef.current, up) || nearestNode([...md.nodes.values()], up);
        if (from) { try { computed = findRoute(md, from.node.id, bt.nodeIds, opts); } catch { computed = null; } }
      } else if (md && ev) {
        // Nearest OUTDOOR node first (fall back to any node), then pathfind
        // through the event's checkpoints to the destination. Destination node:
        // an authored door, else the assigned room, else — for a map-placed pin
        // with only coordinates — the nearest wayfinding node to that pin.
        let destId = ev.destNodeId || assignRef.current[ev.id]?.nodeId;
        if (!destId && ev.lat != null && ev.lng != null) {
          destId = nearestNode([...md.nodes.values()], { lat: ev.lat, lng: ev.lng })?.node.id;
        }
        const checkpoints = (ev.checkpoints || []).map(c => c.nodeId).filter(Boolean);
        if (destId) {
          const from = nearestNode(outdoorNodesRef.current, up) || nearestNode([...md.nodes.values()], up);
          if (from) { try { computed = routeThrough(md, from.node.id, [...checkpoints, destId], opts); } catch { computed = null; } }
        }
      }
      setRoute(computed);
      const pts = computed?.nodes?.length ? computed.nodes.map(n => ({ lat: n.lat, lng: n.lng, floor: dispFloor(n.floor) })) : [];
      plannedRef.current = { destKey, avoid, pts, dest: bt || ev }; // pts=[] ⇒ fallback straight line
      drawTrimmed(plannedRef.current, up);
    };

    const planned = plannedRef.current;
    if (!planned || planned.destKey !== destKey || planned.avoid !== avoid) {
      solve();                                            // new destination / first run ⇒ solve once
    } else if (planned.pts.length && projectOnPath(up, planned.pts).dist > REDRAW_DEVIATION_M) {
      solve();                                            // strayed far from the line ⇒ re-solve
    } else {
      drawTrimmed(planned, up);                           // on track ⇒ just shorten the line
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, graphReady, userPos, nextEvent, assign, bathTarget, data?.avoidTunnels]);

  // Clear route polylines on unmount.
  useEffect(() => () => clearSegs(), []); // eslint-disable-line react-hooks/exhaustive-deps

  // Restyle (bold vs faded) when I switch floors — no re-routing needed.
  useEffect(() => { routeSegsRef.current.forEach(styleSeg); }, [selectedFloor]); // eslint-disable-line react-hooks/exhaustive-deps

  // Marching arrows at a CONSTANT velocity, only on the active-floor segment(s).
  useEffect(() => {
    if (!mapReady) return;
    const REPEAT = 80, VELOCITY = 55; // px between arrows, px/sec
    let raf, last = performance.now(), off = 0;
    const step = (t) => {
      const dt = Math.min((t - last) / 1000, 0.1); last = t;
      off = (off + VELOCITY * dt) % REPEAT;
      for (const s of routeSegsRef.current) {
        if (!(s.floor === 0 || s.floor === selectedFloorRef.current)) continue;
        const icons = s.poly.get('icons');
        if (icons?.[0]) { icons[0].offset = `${off}px`; s.poly.set('icons', icons); }
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [mapReady]);

  const focus = (id) => {
    setFocusedId(id); setListOpen(false); setBathOpen(false); setBackpackOpen(false);
    const ev = displayEvents.find(e => e.id === id);
    if (ev?.lat != null) mapInstanceRef.current?.panTo({ lat: ev.lat, lng: ev.lng });
  };

  // Only real building levels (1, 2, 3…) — no Outside/Tunnel — low→high.
  const orderedFloors = useMemo(() => routeFloors.filter(f => f >= 1).sort((a, b) => a - b), [routeFloors]);
  const floorIdx = orderedFloors.indexOf(selectedFloor);
  // The current "main direction" shown on the collapsed bar (GPS-advanced).
  const leadDir = directions.length ? directions[Math.min(activeDir, directions.length - 1)] : null;

  // Where the user is, for the bottom readout: the building whose footprint
  // contains them, or "Outside" when they aren't inside any building.
  const locHere = useMemo(() => {
    const md = mapDataRef.current;
    if (!userPos || !md) return null;
    for (const b of md.buildings) {
      for (const ring of b.outlines || []) {
        if (ring.length >= 3 && pointInPolygon(userPos, ring)) return { inside: true, name: b.name || b.slug };
      }
    }
    return { inside: false };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPos, graphReady]);
  const selectFloor = (f) => { if (f != null) setSelectedFloor(f); }; // switch in place — never move the camera

  // Pan/zoom to a bathroom (and show its floor) + highlight its room polygon.
  const focusBathroom = (b) => {
    setFocusBath({ slug: b.slug, roomNumber: b.roomNumber, floor: b.floor, lat: b.lat, lng: b.lng, color: BATH_COLORS[b.type] || UM.gold });
    if (b.lat != null) mapInstanceRef.current?.panTo({ lat: b.lat, lng: b.lng });
    if (b.floor != null) setSelectedFloor(dispFloor(b.floor));
  };
  // "See route": preview the path to this bathroom (route override + highlight),
  // then prompt "Go" at the bottom to start turn-by-turn guiding.
  const seeRoute = (b) => {
    setBathTarget({ id: b.id, nodeIds: b.nodeIds, lat: b.lat, lng: b.lng, floor: b.floor, name: b.name, building: b.building, type: b.type });
    setBathGoing(false);
    setBathOpen(false);
    focusBathroom(b);
  };
  const startGoing = () => setBathGoing(true); // commit → guiding mode (step-by-step box)
  const clearBathroom = () => { setBathTarget(null); setBathGoing(false); setFocusBath(null); }; // resume routing to the next event

  // Wayfind to a backpack resource's pickup location (reuses the guiding
  // override, tagged kind:'resource' so the guide box shows the item's info).
  const goToResource = (res) => {
    const md = mapDataRef.current;
    const node = res.destNodeId && md?.nodes.get(res.destNodeId);
    if (!node) return;
    setBathTarget({ id: res.id, kind: 'resource', icon: res.icon, name: res.name || 'Resource',
      nodeIds: [res.destNodeId], lat: node.lat, lng: node.lng, floor: node.floor ?? null, building: res.location });
    setBathGoing(true);
    setBackpackOpen(false);
    setFocusBath({ slug: node.building, roomNumber: node.room_number, floor: node.floor ?? null, lat: node.lat, lng: node.lng, color: UM.gold });
    if (node.lat != null) mapInstanceRef.current?.panTo({ lat: node.lat, lng: node.lng });
    if (node.floor != null) setSelectedFloor(dispFloor(node.floor));
  };

  // "Go" from an event's detail sheet: a TEMPORARY route override to that event
  // (same mechanism as bathrooms/resources, tagged kind:'event'). Stop guiding
  // and it falls back to routing to the next event by time.
  const goToEvent = (ev) => {
    if (!ev || ev.lat == null) return;
    const md = mapDataRef.current;
    let destId = ev.destNodeId || assign[ev.id]?.nodeId;
    if (!destId && md) destId = nearestNode([...md.nodes.values()], { lat: ev.lat, lng: ev.lng })?.node.id;
    const node = destId ? md?.nodes.get(destId) : null;
    const floor = node?.floor ?? null;
    const pan = { lat: node?.lat ?? ev.lat, lng: node?.lng ?? ev.lng };
    setBathTarget({ id: ev.id, kind: 'event', icon: ev.icon || '📍', name: ev.title || 'Event',
      nodeIds: destId ? [destId] : [], lat: pan.lat, lng: pan.lng, floor, building: ev.location });
    setBathGoing(true);
    setFocusedId(null);
    mapInstanceRef.current?.panTo(pan);
    if (floor != null) setSelectedFloor(dispFloor(floor));
  };

  if (!apiKey) return <div className="map-no-key"><h3>Google Maps API Key Required</h3><p>Add <code>REACT_APP_MAPS_KEY</code> to your <code>.env.local</code>.</p></div>;
  if (loadError) return <div className="map-no-key"><h3>Failed to load Google Maps</h3></div>;

  return (
    <div className="wd-app">
      <div className="wd-phone" ref={phoneRef}>
        <div className="wd-map" ref={mapRef} />

        {/* Blue brand header */}
        <header className="wd-header">
          <span className="wd-logo-chip"><img className="wd-logo" src={umLogo} alt="University of Manitoba" /></span>
          <button className="wd-help" onClick={() => setHelpOpen(true)} aria-label="Help">?</button>
        </header>

        {/* Next-up island */}
        <div className={`wd-island ${listOpen ? 'open' : ''}`}>
          <div className="wd-island-bar" ref={islandBarRef}>
            <button className="wd-island-main" onClick={() => nextEvent && focus(nextEvent.id)}>
              <div className="wd-island-tagrow">
                <span className="wd-island-tag">NEXT UP</span>
                {nextEvent && countdownText(nextEvent.time, tick) && (
                  <span className="wd-island-count">{countdownText(nextEvent.time, tick)}</span>
                )}
              </div>
              {nextEvent ? (
                <>
                  <div className="wd-island-name"><span className="wd-island-emoji">{nextEvent.icon || '📍'}</span>{nextEvent.title || 'Untitled'}</div>
                  <div className="wd-island-meta">
                    <span>🕒 {prettyTime(nextEvent.time)}</span>
                    {nextEvent.location && <span>📍 {nextEvent.location}</span>}
                    {islandDist && <span className="wd-island-dist">{islandDist}</span>}
                  </div>
                </>
              ) : <div className="wd-island-name">No events yet</div>}
            </button>
            <button className="wd-island-chev" onClick={() => setListOpen(o => !o)} aria-label="Show all events">
              <span className={`wd-chev ${listOpen ? 'up' : ''}`}>⌄</span>
            </button>
          </div>

          {listOpen && (
            <div className="wd-island-list">
              <div className="wd-island-list-head">Full itinerary</div>
              {displayEvents.map(ev => {
                const isCurrent = ev.id === nextEvent?.id;
                return (
                  <button key={ev.id} className={`wd-row ${isCurrent ? 'current' : 'dim'} ${ev.id === focusedId ? 'active' : ''}`} onClick={() => focus(ev.id)}>
                    {isCurrent && <span className="wd-row-badge">NOW</span>}
                    <span className="wd-row-time">{prettyTime(ev.time)}</span>
                    <span className="wd-row-emoji">{ev.icon || '📍'}</span>
                    <span className="wd-row-text">
                      <span className="wd-row-title">{ev.title || 'Untitled'}</span>
                      {ev.location && <span className="wd-row-loc">{ev.location}</span>}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Resources backpack (top-right). Opening it closes the bathroom menu.
            `.open` lifts the whole stacking context so the modal (rendered inside
            this div) paints above the side buttons / directions, not behind them. */}
        <div className={`wd-fab ${backpackOpen ? 'open' : ''} ${!bpSeen ? 'pulse' : ''}`}>
          <WelcomeBackpack resources={data?.resources || []}
            open={backpackOpen} onGo={goToResource}
            onOpenChange={(v) => {
              setBackpackOpen(v);
              if (v) {
                setBathOpen(false);
                if (!bpSeen) { setBpSeen(true); markHint('backpack'); setBpHintOpen(true); }
              }
            }} />
        </div>

        {/* Bathroom finder — side button + popup list (filter tabs + colours).
            Opening it closes the backpack, so only one menu is ever open. */}
        <button className={`wd-bath-btn ${bathTarget ? 'active' : ''}`}
          onClick={() => { setBathFilter(null); setBackpackOpen(false); setBathOpen(o => !o); }} aria-label="Find bathrooms">🚻</button>

        {bathOpen && (
          <div className="wd-bath-pop">
            <button className="wd-bath-grab" onClick={() => setBathOpen(false)} aria-label="Dismiss" />
            <div className="wd-bath-head">
              <span>Closest bathrooms</span>
              <button className="wd-bath-close" onClick={() => setBathOpen(false)} aria-label="Close">✕</button>
            </div>
            <div className="wd-bath-tabs">
              {[['mens', "Men's"], ['womens', "Women's"], ['accessible', '♿ Accessible']].map(([k, l]) => (
                <button key={k} className={`wd-bath-tab ${bathFilter === k ? 'on' : ''}`} onClick={() => setBathFilter(f => f === k ? null : k)}>{l}</button>
              ))}
            </div>
            <div className="wd-bath-list">
              {shownBathrooms.length === 0 && <div className="wd-bath-empty">No matching bathrooms nearby.</div>}
              {shownBathrooms.slice(0, 12).map(b => {
                const active = bathTarget?.id === b.id;
                const col = BATH_COLORS[b.type] || '#455a64';
                return (
                  <div key={b.id} className={`wd-bath-row ${active ? 'active' : ''}`}>
                    <button className="wd-bath-info" onClick={() => focusBathroom(b)}>
                      <span className="wd-bath-type" style={{ color: col }}>
                        <span className="wd-bath-dot" style={{ background: col }} />
                        {BATH_LABELS[b.type] || 'Bathroom'}{b.accessible ? ' ♿' : ''}
                      </span>
                      <span className="wd-bath-loc">
                        {b.building || 'Unknown'}
                        {b.floor != null ? ` · Level ${dispFloor(b.floor) * 100}` : ''}
                        {b.roomNumber ? ` · ${b.roomNumber}` : ''}
                      </span>
                      {b.periodSupply && <span className="wd-bath-period">🩸 Menstrual products available</span>}
                    </button>
                    {b.dist != null && <span className="wd-bath-dist">{fmtDist(b.dist)}</span>}
                    <button className="wd-bath-go" onClick={() => seeRoute(b)} disabled={!b.nodeIds?.length}>
                      {active ? '✓' : 'See route'}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Preview prompt — route is shown; tap Go to start guiding */}
        {bathTarget && !bathGoing && (
          <div className="wd-bath-prompt">
            <span className="wd-bath-prompt-ic" style={{ color: BATH_COLORS[bathTarget.type] || '#455a64' }}>🚻</span>
            <span className="wd-bath-prompt-info">
              <span className="wd-bath-prompt-t">{bathTarget.building || 'Bathroom'}</span>
              <span className="wd-bath-prompt-s">
                {[
                  BATH_LABELS[bathTarget.type] || 'Bathroom',
                  bathTarget.floor != null ? `Level ${dispFloor(bathTarget.floor) * 100}` : null,
                  (userPos && bathTarget.lat != null) ? fmtDist(haversine(userPos, bathTarget)) : null,
                ].filter(Boolean).join(' · ')}
              </span>
            </span>
            <button className="wd-bath-prompt-x" onClick={clearBathroom} aria-label="Cancel">✕</button>
            <button className="wd-bath-prompt-go" onClick={startGoing}>Go</button>
          </div>
        )}

        {/* Event detail sheet */}
        {focused && (
          <div className="wd-sheet" key={focused.id}>
            <button className="wd-sheet-x" onClick={() => setFocusedId(null)} aria-label="Close">✕</button>
            {focused.image && <div className="wd-sheet-img" style={{ backgroundImage: `url('${picUrl(focused.image)}')` }} />}
            <div className="wd-sheet-body">
              <div className="wd-sheet-time">🕒 {prettyTime(focused.time)}</div>
              <div className="wd-sheet-title"><span>{focused.icon || '📍'}</span> {focused.title || 'Event'}</div>
              {focused.location && <div className="wd-sheet-loc">📍 {focused.location}</div>}
              {focused.description && <p className="wd-sheet-desc">{focused.description}</p>}
              {focused.lat != null && (
                <button className="wd-sheet-go" onClick={() => goToEvent(focused)}>Go to this event →</button>
              )}
            </div>
          </div>
        )}

        {/* Right-side control stack: floor switch (when a route goes indoors),
            then the follow button directly below it. */}
        <div className="wd-rightctl">
          {orderedFloors.length > 0 && (
            <div className="wd-floorctl">
              <button className="wd-floorctl-btn" disabled={floorIdx >= orderedFloors.length - 1}
                onClick={() => selectFloor(orderedFloors[floorIdx + 1])} aria-label="Up a level">▲</button>
              <div className="wd-floorctl-label" style={{ background: floorColor(selectedFloor) }}>
                <span className="wd-floorctl-word">LEVEL</span>
                <span className="wd-floorctl-num">{(selectedFloor >= 1 ? selectedFloor : 1) * 100}</span>
              </div>
              <button className="wd-floorctl-btn" disabled={floorIdx <= 0}
                onClick={() => selectFloor(orderedFloors[floorIdx - 1])} aria-label="Down a level">▼</button>
            </div>
          )}
          {/* Follow mode — recenter on the user + rotate the map to their heading. */}
          <button className={`wd-followbtn ${follow ? 'active' : ''}`} onClick={toggleFollow}
            aria-label="Center on my location and follow my heading" aria-pressed={follow}>
            <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
              <path d="M12 3.4 19 20l-7-3.2L5 20z" fill="currentColor" stroke="currentColor"
                strokeWidth="1.4" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        {/* Step-by-step directions to the next EVENT — hidden while guiding to a
            bathroom (that gets its own box below) or previewing a route. */}
        {directions.length > 0 && !bathTarget && (
          <div className={`wd-directions ${dirOpen ? 'open' : ''}`}>
            {dirOpen && (
              <ol className="wd-dir-list">
                {directions.map(d => (
                  <li
                    key={d.id}
                    id={`wd-dir-${d.id}`}
                    className={`wd-dir-step${d.at ? ' clickable' : ''}${d.id === highlightStep ? ' hl' : ''}`}
                    onClick={d.at ? (e) => { e.currentTarget.scrollIntoView({ block: 'center', behavior: 'smooth' }); handleDirClick(d); } : undefined}
                    title={d.at ? 'Show on map' : undefined}
                  >
                    <span className="wd-dir-step-ic">{d.icon}</span>
                    <span className="wd-dir-step-tx">{d.text}</span>
                    {d.chip && <span className="wd-dir-step-lvl">{d.chip}</span>}
                  </li>
                ))}
              </ol>
            )}
            <button className="wd-dir-bar" onClick={toggleDir} aria-expanded={dirOpen}>
              <span className="wd-dir-ic">🧭</span>
              <span className="wd-dir-lead-col">
                {nextEvent?.title && <span className="wd-dir-event">{nextEvent.icon || '📍'} To {nextEvent.title}</span>}
                <span className="wd-dir-lead">
                  <span className="wd-dir-lead-ic">{leadDir.icon}</span>
                  {leadDir.text}
                </span>
              </span>
              {leadDir.chip && <span className="wd-dir-step-lvl">{leadDir.chip}</span>}
              <span className={`wd-chev ${dirOpen ? 'up' : ''}`}>⌄</span>
            </button>
          </div>
        )}

        {/* Guiding to a bathroom — same step-by-step box (middle-bottom) with a
            "Guiding to bathroom" label and a Stop button on the side. */}
        {bathTarget && bathGoing && directions.length > 0 && (
          <div className={`wd-directions wd-guide ${dirOpen ? 'open' : ''}`}>
            {dirOpen && (
              <ol className="wd-dir-list">
                {directions.map(d => (
                  <li
                    key={d.id}
                    id={`wd-dir-${d.id}`}
                    className={`wd-dir-step${d.at ? ' clickable' : ''}${d.id === highlightStep ? ' hl' : ''}`}
                    onClick={d.at ? (e) => { e.currentTarget.scrollIntoView({ block: 'center', behavior: 'smooth' }); handleDirClick(d); } : undefined}
                    title={d.at ? 'Show on map' : undefined}
                  >
                    <span className="wd-dir-step-ic">{d.icon}</span>
                    <span className="wd-dir-step-tx">{d.text}</span>
                    {d.chip && <span className="wd-dir-step-lvl">{d.chip}</span>}
                  </li>
                ))}
              </ol>
            )}
            <div className="wd-guide-bar">
              <button className="wd-guide-main" onClick={toggleDir} aria-expanded={dirOpen}>
                <span className="wd-guide-ic" style={{ background: `${guideView?.color || '#455a64'}22`, color: guideView?.color || '#455a64' }}>{guideView?.icon || '🚻'}</span>
                <span className="wd-guide-lead">
                  <span className="wd-guide-title">{guideView?.title || 'Guiding'}</span>
                  {guideView?.sub && <span className="wd-guide-sub">{guideView.sub}</span>}
                </span>
                <span className={`wd-chev ${dirOpen ? 'up' : ''}`}>⌄</span>
              </button>
              <button className="wd-guide-stop" onClick={clearBathroom} aria-label="Stop guiding">✕<span className="wd-guide-stop-l">Stop</span></button>
            </div>
          </div>
        )}

        {/* Weak/loading GPS — small caption tucked into the bottom-left corner. */}
        {gpsWeak && (
          <div className="wd-gps-corner">
            <span className="wd-gps-dot" />
            GPS loading, location may be off{gpsAcc != null ? ` (±${Math.round(gpsAcc)} m)` : ''}
          </div>
        )}

        {/* Bottom "where you are" readout (centered). */}
        {locHere && (
          <div className="wd-botinfo">
            <div className="wd-locbar">
              {locHere.inside ? (
                <>
                  <span className="wd-locbar-bldg">{locHere.name}</span>
                  <span className="wd-locbar-lvl">Level {(selectedFloor >= 1 ? selectedFloor : 1) * 100}</span>
                </>
              ) : <span className="wd-locbar-bldg">Outside</span>}
            </div>
          </div>
        )}

        {/* Help */}
        {helpOpen && (
          <div className="wd-modal-backdrop" onClick={() => setHelpOpen(false)}>
            <div className="wd-help-card" onClick={(e) => e.stopPropagation()}>
              <button className="wd-help-x" onClick={() => setHelpOpen(false)} aria-label="Close">✕</button>
              <h3>Welcome Day</h3>
              <ul className="wd-help-list">
                <li><strong>Next up</strong> shows your upcoming event with a live countdown and distance.</li>
                <li>Tap the <strong>▾ chevron</strong> to see the full itinerary; tap any event to view details.</li>
                <li>The <strong>blue line</strong> routes you from your location to the next event. The arrows point the way.</li>
                <li>Tap the <strong>🎒 backpack</strong> for your student resources.</li>
              </ul>
            </div>
          </div>
        )}

        {/* First-launch pointer: how room numbers map to levels. */}
        {welcomeOpen && (
          <div className="wd-modal-backdrop" onClick={closeWelcome}>
            <div className="wd-welcome" onClick={(e) => e.stopPropagation()}>
              <button className="wd-welcome-x" onClick={closeWelcome} aria-label="Close">✕</button>
              <h2 className="wd-welcome-title">Finding your way around</h2>

              <div className="wd-fg2">
                <span className="wd-fg2-room"><span className="wd-fg2-d1">2</span>13</span>
                <span className="wd-fg2-arrow">→</span>
                <span className="wd-fg2-lvl">Level 200</span>
              </div>

              <p className="wd-welcome-body">
                Not sure what floor you are on? Look at any nearby room number, the first digit is the level!
              </p>
              <button className="wd-welcome-go" onClick={closeWelcome}>Got it</button>
            </div>
          </div>
        )}

        {/* Pointer shown the first time the directions box is opened. */}
        {dirHintOpen && (
          <div className="wd-hint-backdrop" onClick={() => setDirHintOpen(false)}>
            <div className="wd-hint" onClick={(e) => e.stopPropagation()}>
              <span className="wd-hint-ic">👆</span>
              <div className="wd-hint-tx">Tap any direction in the list to move the map to that spot.</div>
              <button className="wd-hint-ok" onClick={() => setDirHintOpen(false)}>Got it</button>
            </div>
          </div>
        )}

        {/* Pointer shown the first time the backpack is opened. */}
        {bpHintOpen && (
          <div className="wd-bphint-backdrop" onClick={() => setBpHintOpen(false)}>
            <div className="wd-bphint" onClick={(e) => e.stopPropagation()}>
              <span className="wd-bphint-ic">🎒</span>
              <div className="wd-bphint-tx">These are resources to grab sometime this week. Tap any item to learn more about it.</div>
              <button className="wd-bphint-ok" onClick={() => setBpHintOpen(false)}>Got it</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
